/**
 * "Waiting on you" — the person's to-dos from the server summary (ADR-0070),
 * newest first, worded and linked exactly as in the bell
 * (`app/waitingNotifications.ts`). Up to six rows; the rest are one click away
 * on the Notifications page.
 */

import {
  Anchor,
  Badge,
  Divider,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconCircleCheck } from "@tabler/icons-react";
import { Fragment } from "react";
import { Link as RouterLink } from "react-router-dom";
import { notificationColor, notificationIcon, notificationWhen } from "@/app/notificationMeta";
import { waitingNotification } from "@/app/waitingNotifications";
import type { WaitingItem } from "@/shared/api/waiting";
import { HomeRow } from "./HomeRow";

const SHOWN = 6;

export function WaitingCard({ items, failed }: { items: WaitingItem[] | null; failed: boolean }) {
  const rows = (items ?? []).map(waitingNotification);

  return (
    <Paper withBorder radius="md" p="lg">
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <Title order={4}>Waiting on you</Title>
          {rows.length > 0 && (
            <Badge
              circle
              size="lg"
              color="brand"
              variant="filled"
              aria-label={`${rows.length} waiting`}
            >
              {rows.length}
            </Badge>
          )}
        </Group>
        <Anchor component={RouterLink} to="/notifications" size="sm">
          See all notifications
        </Anchor>
      </Group>

      {items === null ? (
        failed ? (
          <Text size="sm" c="dimmed">
            Could not load what is waiting on you. It will try again in a minute.
          </Text>
        ) : (
          <Group justify="center" p="md">
            <Loader size="sm" />
          </Group>
        )
      ) : rows.length === 0 ? (
        <Group gap="sm" py="sm">
          <ThemeIcon variant="light" color="green" size={36} radius="md">
            <IconCircleCheck size={20} />
          </ThemeIcon>
          <Text size="sm" c="dimmed">
            Nothing is waiting on you.
          </Text>
        </Group>
      ) : (
        <Stack gap={0}>
          {rows.slice(0, SHOWN).map((n, i) => (
            <Fragment key={n.id}>
              {i > 0 && <Divider />}
              <HomeRow
                to={n.to}
                icon={notificationIcon(n, 18)}
                color={notificationColor(n)}
                title={n.title}
                detail={[n.detail, notificationWhen(n.at)].filter(Boolean).join(" · ")}
              />
            </Fragment>
          ))}
          {rows.length > SHOWN && (
            <Text size="sm" c="dimmed" pt="xs">
              and {rows.length - SHOWN} more on the{" "}
              <Anchor component={RouterLink} to="/notifications" size="sm">
                Notifications page
              </Anchor>
            </Text>
          )}
        </Stack>
      )}
    </Paper>
  );
}
